import { useCallback, useState } from "react";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import RadioButtons from "@imminently/decisively-ui/RadioButtons";
import { useTranslation } from "react-i18next";
import Button from "@imminently/decisively-ui/Button";
import Dialog, { useDialogContext } from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import EmptyStatus from "@imminently/decisively-ui/EmptyStatus";
import SectionLabel from "@imminently/decisively-ui/SectionLabel";
import { releaseService } from "@/app/providers/services";
import { isFeatureEnabled } from "@/config/features";
import { useScrollable } from "@/hooks/use-scrollable";

import type { Release, ReleaseEnvironment } from "@packages/commons";
import { ReleaseCard } from "@/ui/blocks/cards/ReleaseCard";
import { CardSkeleton } from "@/ui/blocks/cards/CardSkeleton";
import { useViewRelease } from "../hooks/useRelease";
import { useImportReleaseModal, useReleaseModal } from "../hooks/useReleaseModals";
import { useScope } from "@/redux/scope";

// ─── Tab Content ─────────────────────────────────────────────────────────────

interface ReleasesTabContentProps {
  projectId: string;
  env: ReleaseEnvironment;
  onView: (id: string) => void;
  className?: string;
  title?: string;
  scrollable?: boolean;
}

const ReleasesTabContent = ({
  projectId,
  env,
  onView,
  className,
  title,
  scrollable = true,
}: ReleasesTabContentProps) => {
  const { t } = useTranslation();
  const { data: releases, total, query, loadMore } = releaseService.useInfiniteResources({
    perPage: 12,
    enabled: Boolean(projectId),
    filter: {
      model: projectId,
      env,
      sort: { field: "releaseNo", order: "desc" },
      fields: ["id", "releaseNo", "env", "description", "activeVersionFlag", "model"],
    },
    refetchOnWindowFocus: false,
  });

  const canLoad = !query.isError && !query.isFetching && (query.hasNextPage ?? false);
  const scrollProps = useScrollable(loadMore, canLoad);

  const isLoading = query.isLoading;
  const isEmpty = !isLoading && total === 0;

  const content = (
    <>
      {title ? (
        <SectionLabel title={title} resourceCount={!isLoading ? total : undefined}>
          {query.isFetching && <LoadingSpinner size="xs" />}
        </SectionLabel>
      ) : null}
      {isEmpty ? (
        <EmptyStatus>
          <EmptyStatus.Title>{t("release_centre.no_releases_label")}</EmptyStatus.Title>
        </EmptyStatus>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {isLoading
            ? Array.from({ length: 6 }, (_, i) => <CardSkeleton key={i} />)
            : releases.map((r) => (
              <ReleaseCard
                key={r.id}
                release={r as unknown as Release}
                onView={() => onView(r.id)}
              />
            ))}
          {query.isFetchingNextPage &&
            Array.from({ length: 3 }, (_, i) => <CardSkeleton key={`more-${i}`} />)}
        </div>
      )}
    </>
  );

  return (
    <section className={className}>
      {scrollable ? (
        <div className="flex-1 min-h-0 scrollable py-4 px-6" {...scrollProps}>
          {content}
        </div>
      ) : (
        <div className="py-4 px-6">
          {content}
        </div>
      )}
    </section>
  );
};

// ─── Modal ────────────────────────────────────────────────────────────────────

interface BrowseReleasesModalProps {
  projectId: string;
  close: () => void;
}

export const BrowseReleasesModal = ({ projectId, close }: BrowseReleasesModalProps) => {
  const { t } = useTranslation();
  const { project } = useScope();
  const dialogContext = useDialogContext();
  const openNew = useReleaseModal();
  const openImport = useImportReleaseModal();
  const activateRelease = useViewRelease();
  const [tab, setTab] = useState<ReleaseEnvironment>("test");
  // TODO post canary release - decide what we are doing with tabs and remove this flag
  const releaseCentreTabsEnabled = true; // isFeatureEnabled("experimental.release_centre_tabs");

  const handleView = useCallback(
    (id: string) => {
      let navigateProject: string | undefined = undefined;
      const onProjectsPage = /^\/projects/.test(window.location.pathname);
      // if we are changing project, include the id, or if we are on the projects page
      if (project !== projectId || onProjectsPage) {
        navigateProject = projectId;
      }
      activateRelease(id, navigateProject);
      dialogContext?.close();
    },
    [activateRelease, dialogContext, project, projectId],
  );

  return (
    <Dialog.Content className="flex flex-col">
      <DialogHeader
        title={t("release_centre.title")}
        description={t("release_centre.description_label")}
      />

      {/* using custom body with p-0 as we need to change what controls scroll */}
      <Dialog.Body asChild>
        {releaseCentreTabsEnabled ? (
          <div className="flex flex-col flex-1 overflow-hidden gap-0 p-0">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3 px-6 py-2">
              <RadioButtons
                name="release-environment"
                value={tab}
                onValueChange={(value) => setTab(value as ReleaseEnvironment)}
                options={[
                  {
                    value: "test",
                    label: t("release_centre.development_environment"),
                  },
                  {
                    value: "prod",
                    label: t("release_centre.production_environment"),
                  },
                ]}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="contained"
                  intent="secondary"
                  label={t("release_centre.import_release")}
                  onClick={() => openImport(projectId)}
                />
                {/* TEMP disable this until we have the discussion */}
                {/* <Button
                  type="button"
                  label={t("release_centre.new_release")}
                  onClick={() => openNew()}
                /> */}
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              {tab === "test" ? (
              <ReleasesTabContent
                projectId={projectId}
                className="h-full flex flex-col"
                env="test"
                onView={handleView}
              />
              ) : (
                <ReleasesTabContent
                  projectId={projectId}
                  env="prod"
                  className="h-full flex flex-col"
                  onView={handleView}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-hidden gap-0 p-0">
            <div className="flex items-center justify-end gap-2 px-6 py-2">
              <Button
                type="button"
                variant="contained"
                intent="secondary"
                label={t("release_centre.import_release")}
                onClick={() => openImport(projectId)}
              />
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <ReleasesTabContent
                projectId={projectId}
                env="test"
                onView={handleView}
                title={t("release_centre.development_environment")}
                className="border-b"
                scrollable={false}
              />
              <ReleasesTabContent
                projectId={projectId}
                env="prod"
                onView={handleView}
                title={t("release_centre.production_environment")}
                scrollable={false}
              />
            </div>
          </div>
        )}
      </Dialog.Body>
    </Dialog.Content>
  );
};
